import React, { useState, useEffect, useRef } from "react";
import { Modal, Backdrop, Fade, CircularProgress, makeStyles, Tooltip, IconButton } from "@material-ui/core";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";
import PriorityHighIcon from "@material-ui/icons/PriorityHigh";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import CodeOutlinedIcon from "@material-ui/icons/CodeOutlined";
import ButtonBB from "@View/components/buttons/ButtonBB";
import { downloadFile } from "@Dao/api";
import getAPI from "@API/index";
import { IsPublicRatingV2 } from "@Constants/tokens";

const INDUSTRIES = [
  "Автомобилестроение",
  "АПК",
  "Горная добыча",
  "Жилое строительство (девелопмент)",
  "ЖКХ",
  "Инженерные сети",
  "Информационные технологии и сервисы",
  "Инфраструктура транспортная",
  "Коммерческая недвижимость (арендный бизнес)",
  "Лесная промышленность",
  "Машиностроение",
  "Металлургия",
  "Минеральные удобрения",
  "Нефтегазодобыча",
  "Нефтепереработка",
  "Нефтесервисный бизнес",
  "Оборонно-промышленный комплекс",
  "Оптовая торговля",
  "Прочее строительство (кроме жилищного)",
  "Розничная торговля",
  "Телекоммуникации",
  "Транспорт",
  "Химическая промышленность",
  "Электрогенерация",
];

const POLL_INTERVAL_MS = 3000;

const BLUE = "#6FA4F2";
const GREEN = "#3DA66B";
const RED = "rgba(164, 36, 28, 1)";
const MUTED = "#A1ADBA";
const BORDER = "#E1E6F1";

const RATING_STATUS_LABELS = {
  New: "Не сформирован",
  InProgress: "Формируется",
  Completed: "Сформирован",
  Failed: "Ошибка",
};

const RATING_STATUS_STYLES = {
  New: { bg: "#EBF4FF", color: "#1D6FA4" },
  InProgress: { bg: "#FFF4E5", color: "#B45309" },
  Completed: { bg: "#ECFDF5", color: "#065F46" },
  Failed: { bg: "#FEF2F2", color: "#991B1B" },
};

const getRatingStatusStyle = (status) =>
  RATING_STATUS_STYLES[status] || { bg: "#F3F4F6", color: "#6B7280" };

const StatusPill = ({ status }) => {
  const s = getRatingStatusStyle(status);
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      padding: "3px 10px 3px 7px",
      borderRadius: 20,
      background: s.bg,
      color: s.color,
      fontSize: 12,
      fontWeight: 600,
      lineHeight: "18px",
      whiteSpace: "nowrap",
    }}>
      {RATING_STATUS_LABELS[status] ?? status}
    </span>
  );
};

const useStyles = makeStyles(() => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "460px",
    position: "relative",
    outline: "none",
    overflow: "hidden",
  },
  header: {
    padding: "22px 30px 16px",
    borderBottom: `1px solid ${BORDER}`,
  },
  closeBtn: {
    position: "absolute",
    right: "20px",
    top: "20px",
    cursor: "pointer",
    color: "#8BA0B6",
  },
  title: {
    margin: 0,
    fontWeight: 600,
    fontSize: "20px",
    lineHeight: "24px",
    color: "#1A1E24",
  },
  subtitle: {
    margin: "4px 0 0",
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "17px",
    color: MUTED,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  stepper: {
    display: "flex",
    alignItems: "center",
    padding: "12px 30px",
    background: "#FAFBFC",
    borderBottom: `1px solid ${BORDER}`,
  },
  step: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flex: "0 0 auto",
  },
  circle: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: `2px solid ${BORDER}`,
    color: MUTED,
    background: "#fff",
    flexShrink: 0,
    transition: "all .2s ease",
  },
  circleDone: { borderColor: BLUE, background: BLUE, color: "#fff" },
  circleActive: { borderColor: BLUE, color: BLUE, boxShadow: `0 0 0 3px rgba(111, 164, 242, 0.15)` },
  circleSuccess: { borderColor: GREEN, background: GREEN, color: "#fff" },
  circleError: { borderColor: RED, background: RED, color: "#fff" },
  stepLabel: {
    fontWeight: 500,
    fontSize: "12px",
    lineHeight: "15px",
    whiteSpace: "nowrap",
    color: MUTED,
  },
  stepLabelActive: { color: "#1A1E24" },
  connector: {
    flex: 1,
    height: "2px",
    minWidth: "12px",
    background: BORDER,
    margin: "0 8px",
    borderRadius: "1px",
    transition: "background .2s ease",
  },
  connectorFilled: { background: BLUE },
  body: {
    padding: "20px 30px 8px",
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    boxSizing: "border-box",
    // Ровная сетка: строка по высоте самого высокого контрола (поле «Отрасль», 32px)
    minHeight: "44px",
    padding: "6px 0",
    borderBottom: `1px solid ${BORDER}`,
  },
  infoLabel: {
    fontWeight: 500,
    fontSize: "13px",
    lineHeight: "16px",
    color: MUTED,
    flexShrink: 0,
    marginRight: "12px",
  },
  infoValue: {
    fontWeight: 600,
    fontSize: "13px",
    lineHeight: "16px",
    color: "#1A1E24",
    textAlign: "right",
    wordBreak: "break-word",
  },
  ratingBadge: {
    display: "inline-block",
    padding: "3px 14px",
    borderRadius: "8px",
    border: `1px solid ${BORDER}`,
    background: "#fff",
    fontWeight: 700,
    fontSize: "13px",
    lineHeight: "20px",
    color: "#1A1E24",
  },
  descriptionBlock: {
    margin: "0 30px 12px",
    padding: "12px 14px",
    borderRadius: "8px",
    background: "#F8F9FB",
    fontWeight: 500,
    fontSize: "13px",
    lineHeight: "19px",
    color: "#54606F",
    wordBreak: "break-word",
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "20px 30px 26px",
  },
}));

const formatDate = (value) =>
  value ? new Date(value).toLocaleString("ru-RU") : "—";

const formatScore = (value) =>
  typeof value === "number" ? value.toFixed(2) : "—";

const PublicRatingModal = ({ lead, open, handleClose }) => {
  const classes = useStyles();

  const [rating, setRating] = useState(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [industry, setIndustry] = useState("");
  const pollRef = useRef(null);

  useEffect(() => {
    setIndustry(rating?.rating?.industry || "");
  }, [rating?.rating?.industry]);

  const isInProgress = rating != null && (rating.status === "New" || rating.status === "InProgress");
  const isCompleted = rating != null && rating.status === "Completed";
  const isFailed = rating != null && rating.status === "Failed";
  const isNotStarted = rating == null;

  const activeIndex = (isCompleted || isFailed) ? 2 : isInProgress ? 1 : 0;

  const clearPoll = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const fetchRating = (leadId, { withSpinner } = {}) => {
    if (withSpinner) setLoading(true);
    return getAPI()
      .request({ method: "get", url: `/api/public-rating/lead/${leadId}/latest`, suppressError: true, suppressLoading: true })
      .then((response) => {
        const data = response?.data ?? null;
        setRating(data);
        return data;
      })
      .catch(() => null)
      .finally(() => { if (withSpinner) setLoading(false); });
  };

  useEffect(() => {
    if (!open || !lead?.id) {
      setRating(null);
      clearPoll();
      return;
    }
    fetchRating(lead.id, { withSpinner: true });
    return clearPoll;
  }, [open, lead?.id]);

  useEffect(() => {
    if (!open || !lead?.id) return;
    if (isInProgress && !pollRef.current) {
      pollRef.current = setInterval(() => {
        fetchRating(lead.id).then((data) => {
          if (!data || data.status === "Completed" || data.status === "Failed") clearPoll();
        });
      }, POLL_INTERVAL_MS);
    }
    if (!isInProgress) clearPoll();
  }, [open, lead?.id, isInProgress]);

  const handleCalculate = () => {
    if (!lead?.id) return;
    const isCustomIndustry = !!industry && !INDUSTRIES.includes(industry);
    setStarting(true);
    getAPI()
      .request({
        method: "post",
        url: IsPublicRatingV2() ? `/api/public-rating/v2/lead/${lead.id}` : `/api/public-rating/lead/${lead.id}`,
        data: { industry: industry || null, customIndustry: isCustomIndustry },
        suppressError: false,
        suppressLoading: true,
      })
      .then(() => fetchRating(lead.id))
      .catch(() => null)
      .finally(() => setStarting(false));
  };

  const handleExportExcel = () => {
    if (!rating?.id) return;
    downloadFile(`/api/public-rating/${rating.id}/file`);
  };

  const handleExportJson = () => {
    if (!rating?.id) return;
    downloadFile(`/api/public-rating/${rating.id}/json`);
  };

  if (!lead) return null;

  const clientName = rating?.rating?.leadNameZCB || rating?.rating?.leadName || lead.leadNameZCB || lead.borrowerName || null;
  const description = rating?.rating?.description || null;

  const stages = [
    { label: "Запуск", icon: <DescriptionOutlinedIcon style={{ fontSize: 14 }} /> },
    { label: "Формирование", icon: <AutorenewIcon style={{ fontSize: 14 }} /> },
    { label: "Результат", icon: <CheckIcon style={{ fontSize: 14 }} /> },
  ];

  const renderCircle = (index) => {
    if (index === 2 && (isCompleted || isFailed)) {
      return (
        <div className={`${classes.circle} ${isCompleted ? classes.circleSuccess : classes.circleError}`}>
          {isCompleted ? <CheckIcon style={{ fontSize: 15 }} /> : <PriorityHighIcon style={{ fontSize: 15 }} />}
        </div>
      );
    }
    if (index < activeIndex) {
      return (
        <div className={`${classes.circle} ${classes.circleDone}`}>
          <CheckIcon style={{ fontSize: 15 }} />
        </div>
      );
    }
    if (index === activeIndex) {
      return (
        <div className={`${classes.circle} ${classes.circleActive}`}>
          {isInProgress && index === 1
            ? <CircularProgress size={12} style={{ color: BLUE }} />
            : stages[index].icon}
        </div>
      );
    }
    return <div className={classes.circle}>{stages[index].icon}</div>;
  };

  const rows = [
    {
      label: "Отрасль",
      render: () => (
        <Autocomplete
          freeSolo
          size="small"
          options={INDUSTRIES}
          value={industry}
          inputValue={industry}
          onChange={(e, newValue) => setIndustry(newValue || "")}
          onInputChange={(e, newValue, reason) => {
            if (reason === "input") setIndustry(newValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Выберите или введите"
              size="small"
              sx={{
                "& .MuiInputBase-root": { fontSize: 13, fontWeight: 600, color: "#1A1E24", height: 32, paddingTop: 0, paddingBottom: 0 },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E1E6F1" },
                "& .MuiInputBase-input::placeholder": { color: "#A1ADBA", opacity: 1 },
              }}
            />
          )}
          sx={{ width: 220 }}
          ListboxProps={{ style: { fontSize: 13 } }}
        />
      ),
    },
    { label: "Дата формирования", value: formatDate(rating?.finishedAt) },
    {
      label: "Статус",
      renderLabel: () => (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span className={classes.infoLabel}>Статус</span>
          {rating != null && (
            <Tooltip title="Выгрузить JSON">
              <span>
                <IconButton
                  onClick={handleExportJson}
                  disabled={loading || isInProgress}
                  size="small"
                  style={{ color: MUTED, border: `1px solid ${BORDER}`, borderRadius: 6, padding: 3 }}
                >
                  <CodeOutlinedIcon style={{ fontSize: 14 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </span>
      ),
      render: () => rating?.status
        ? <StatusPill status={rating.status} />
        : <span className={classes.infoValue}>—</span>,
    },
    { label: "Итоговый балл", value: formatScore(rating?.rating?.totalScore) },
    {
      label: "Рейтинг",
      render: () => rating?.rating?.rating
        ? <span className={classes.ratingBadge}>{rating.rating.rating}</span>
        : <span className={classes.infoValue}>—</span>,
    },
  ];

  return (
    <Modal
      open={open}
      onClose={handleClose}
      className={classes.modal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 300 }}
    >
      <Fade in={open}>
        <div className={classes.paper}>
          <div className={classes.header}>
            <div className={classes.closeBtn} onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </div>
            <h2 className={classes.title}>Предварительный рейтинг</h2>
            {clientName && <p className={classes.subtitle}>{clientName}</p>}
          </div>

          <div className={classes.stepper}>
            {stages.map((stage, index) => (
              <React.Fragment key={index}>
                <div className={classes.step}>
                  {renderCircle(index)}
                  <span className={`${classes.stepLabel} ${index <= activeIndex ? classes.stepLabelActive : ""}`}>
                    {stage.label}
                  </span>
                </div>
                {index < stages.length - 1 && (
                  <div className={`${classes.connector} ${index < activeIndex ? classes.connectorFilled : ""}`} />
                )}
              </React.Fragment>
            ))}
          </div>

          <div className={classes.body}>
            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
                <CircularProgress size={28} />
              </div>
            ) : (
              rows.map((row, i) => (
                <div
                  key={i}
                  className={classes.infoRow}
                  style={i === rows.length - 1 ? { borderBottom: "none" } : undefined}
                >
                  {row.renderLabel ? row.renderLabel() : <span className={classes.infoLabel}>{row.label}</span>}
                  {row.render ? row.render() : <span className={classes.infoValue}>{row.value}</span>}
                </div>
              ))
            )}
          </div>

          {!loading && description && (
            <div className={classes.descriptionBlock}>{description}</div>
          )}

          <div className={classes.footer}>
            {(isNotStarted || isCompleted || isFailed) && (
              <ButtonBB
                onClick={handleCalculate}
                disabled={starting || loading}
                startIcon={
                  starting
                    ? <CircularProgress size={16} />
                    : (isCompleted || isFailed)
                      ? <AutorenewIcon style={{ fontSize: 18 }} />
                      : null
                }
                style={{ width: "100%", borderRadius: "8px", padding: "14px 18px", textTransform: "none" }}
              >
                {(isCompleted || isFailed) ? "Пересчитать" : "Рассчитать"}
              </ButtonBB>
            )}
            <ButtonBB
              onClick={handleExportExcel}
              disabled={!rating?.id || loading || isInProgress}
              startIcon={<DescriptionOutlinedIcon style={{ fontSize: 18 }} />}
              style={{ width: "100%", borderRadius: "8px", padding: "14px 18px", textTransform: "none" }}
            >
              Экспорт в Excel
            </ButtonBB>
          </div>
        </div>
      </Fade>
    </Modal>
  );
};

export default PublicRatingModal;
